import { skipToken, useQuery } from '@apollo/client/react';
import find from 'lodash/find';
import { useEffect, useState } from 'react';
import {
  allRoles,
  membersRoles,
  type AbilityRule,
  type PartnerAbilities,
  type AbilityCan,
} from '@guuru/abilities-common';
import { auth, type User } from '@guuru/auth-web';
import type { ResultOf } from '@guuru/graphql-common/gql';
import fullMenu from '../../../../components/navigation/NavigationMenu/fullMenu';
import hasMenuAccess from '../../../../components/navigation/NavigationMenu/hasMenuAccess';
import ability from '../../../../config/ability';
import GET_USER from './queries/GetFetchCurrentUser';

type FetchedUser = NonNullable<ResultOf<typeof GET_USER>['expert']>;
type Role = FetchedUser['roles'][number];
type RoleName = NonNullable<Role['role']>;

interface CurrentUserAbilities {
  globalAbilities: AbilityRule[];
  partnerAbilities: PartnerAbilities;
}

interface CurrentUser extends Pick<
  FetchedUser,
  'name' | 'email' | 'photoUrl' | 'roles'
> {
  can: AbilityCan;
  abilities: CurrentUserAbilities;
  canAccess: (route: string) => boolean;
  hasGlobalRole: (role: RoleName) => Role | undefined;
  isAccountVerified: FetchedUser['isEmailVerified'];
  hasRole: (role: RoleName, partnerId?: string) => boolean;
  isSuperAdmin: boolean;
  isTosAccepted: boolean;
  partnerIdsWithRoles: string[];
  uid?: string;
}

interface FetchCurrentUserResult {
  loading: boolean;
  user: CurrentUser;
}

const getPartnerRoles = (roles: Role[], partnerId?: string) =>
  roles
    .filter(({ partner }) => partner?.id === partnerId)
    .map(({ role }) => role);

const getPartnerIdsWithMemberRoles = (roles: Role[]) =>
  roles
    .filter(
      ({ role, partner }) =>
        partner !== null && role !== null && role in membersRoles,
    )
    .map(({ partner }) => partner!.id);

const useFirebaseCurrentUserId = () => {
  const [authUser, setAuthUser] = useState<User | null | false>(false);
  useEffect(() => auth.onAuthStateChanged(setAuthUser), []);
  return {
    userId: authUser ? authUser.uid : undefined,
    loading: authUser === false,
  };
};

const useFetchCurrentUser = (userId?: string): FetchCurrentUserResult => {
  const {
    loading,
    error,
    data: { expert } = {},
  } = useQuery(GET_USER, userId ? { variables: { id: userId } } : skipToken);

  if (error) {
    throw error;
  }

  const {
    name = null,
    email = null,
    photoUrl = null,
    roles = [],
    abilities = {},
    isEmailVerified = false,
  } = expert ?? {};

  const partnerIdsWithRoles = getPartnerIdsWithMemberRoles(roles);

  const isLoading = loading || (userId !== undefined && expert === undefined);

  const canAccess = (route: string) => {
    const menu = find(fullMenu, { route });
    if (!menu || !menu.abilities) {
      return false;
    }

    return hasMenuAccess(
      menu,
      (action: string, subject: string, field?: string) =>
        ability.can(action, subject, field),
    );
  };

  const hasRole = (r: RoleName, partnerId?: string) =>
    getPartnerRoles(roles, partnerId).includes(r);

  const hasGlobalRole = (r: RoleName) => roles.find(({ role }) => r === role);

  return {
    loading: isLoading,
    user: {
      hasRole,
      hasGlobalRole,
      canAccess,
      roles,
      abilities,
      name,
      photoUrl,
      partnerIdsWithRoles,
      can: () => false,
      isSuperAdmin: roles.some(({ role }) => role != null && role in allRoles),
      uid: !isLoading ? userId : undefined,
      email: email ?? auth.currentUser?.email ?? null,
      isAccountVerified: isEmailVerified,
      isTosAccepted: true,
    },
  };
};

export default () => {
  const { userId, loading: isLoadingFirebaseUser } = useFirebaseCurrentUserId();
  const { loading: isLoadingUser, user } = useFetchCurrentUser(userId);

  return {
    user,
    loading: isLoadingUser || isLoadingFirebaseUser,
  };
};
